import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  BugReport as BugReportIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import axiosInstance from '../api/axiosInstance';

const LogAnalysisPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [timeRange, setTimeRange] = useState('1h'); // 1h, 24h, 7d
  const [source, setSource] = useState('both');

  // Fetch recent analyses on mount
  useEffect(() => {
    fetchRecentAnalyses();
  }, []);

  const fetchRecentAnalyses = async () => {
    try {
      const response = await axiosInstance.get('/logs/analyses?limit=10');
      if (response.data.success) {
        setAnalyses(response.data.data);
        if (response.data.data.length > 0 && !currentAnalysis) {
          setCurrentAnalysis(response.data.data[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching analyses:', err);
    }
  };

  const handleQuickAnalyze = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.post('/logs/quick-analyze');
      
      if (response.data.success) {
        setCurrentAnalysis(response.data.data);
        fetchRecentAnalyses(); // Refresh the list
      } else {
        setError(response.data.message || 'Analysis failed');
      }
    } catch (err) {
      console.error('Error performing analysis:', err);
      setError(err.response?.data?.message || err.message || 'Failed to analyze logs');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomAnalyze = async () => {
    setLoading(true);
    setError(null);

    try {
      // Calculate time range
      const end = new Date();
      let start = new Date();
      
      switch (timeRange) {
        case '1h':
          start = new Date(end.getTime() - 60 * 60 * 1000);
          break;
        case '24h':
          start = new Date(end.getTime() - 24 * 60 * 60 * 1000);
          break;
        case '7d':
          start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        default:
          start = new Date(end.getTime() - 60 * 60 * 1000);
      }

      const response = await axiosInstance.post('/logs/analyze', {
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        source,
        analysisType: 'manual'
      });
      
      if (response.data.success) {
        setCurrentAnalysis(response.data.data);
        fetchRecentAnalyses(); // Refresh the list
      } else {
        setError(response.data.message || 'Analysis failed');
      }
    } catch (err) {
      console.error('Error performing analysis:', err);
      setError(err.response?.data?.message || err.message || 'Failed to analyze logs');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
      case 'high':
        return <ErrorIcon />;
      case 'medium':
        return <WarningIcon />;
      case 'low':
        return <InfoIcon />;
      default:
        return <CheckCircleIcon />;
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'performance':
        return <SpeedIcon />;
      case 'security':
        return <SecurityIcon />;
      case 'errors':
        return <BugReportIcon />;
      default:
        return <InfoIcon />;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          <AnalyticsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          AI Log Analysis
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Analyze application logs to detect anomalies, identify issues, and gain insights.
        </Typography>

        {/* Control Panel */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth>
                <InputLabel>Time Range</InputLabel>
                <Select
                  value={timeRange}
                  label="Time Range"
                  onChange={(e) => setTimeRange(e.target.value)}
                  disabled={loading}
                >
                  <MenuItem value="1h">Last Hour</MenuItem>
                  <MenuItem value="24h">Last 24 Hours</MenuItem>
                  <MenuItem value="7d">Last 7 Days</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth>
                <InputLabel>Source</InputLabel>
                <Select
                  value={source}
                  label="Source"
                  onChange={(e) => setSource(e.target.value)}
                  disabled={loading}
                >
                  <MenuItem value="both">Both</MenuItem>
                  <MenuItem value="backend">Backend</MenuItem>
                  <MenuItem value="frontend">Frontend</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCustomAnalyze}
                disabled={loading}
                fullWidth
              >
                {loading ? <CircularProgress size={24} /> : 'Analyze'}
              </Button>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Button
                variant="outlined"
                onClick={handleQuickAnalyze}
                disabled={loading}
                fullWidth
              >
                Quick Analyze (1h)
              </Button>
            </Grid>
          </Grid>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Analysis Results */}
        {currentAnalysis && (
          <Box sx={{ mt: 3 }}>
            {/* Summary Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Analysis Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Total Logs
                    </Typography>
                    <Typography variant="h5">
                      {currentAnalysis.summary?.totalLogs || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Errors
                    </Typography>
                    <Typography variant="h5" color="error">
                      {currentAnalysis.summary?.errorCount || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Warnings
                    </Typography>
                    <Typography variant="h5" color="warning.main">
                      {currentAnalysis.summary?.warningCount || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Anomalies Found
                    </Typography>
                    <Typography variant="h5">
                      {currentAnalysis.anomalies?.length || 0}
                    </Typography>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Analysis Period: {new Date(currentAnalysis.startTime).toLocaleString()} - {new Date(currentAnalysis.endTime).toLocaleString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Anomalies Section */}
            {currentAnalysis.anomalies && currentAnalysis.anomalies.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Anomalies Detected ({currentAnalysis.anomalies.length})
                  </Typography>
                  <List>
                    {currentAnalysis.anomalies.map((anomaly, index) => (
                      <React.Fragment key={index}>
                        <ListItem alignItems="flex-start">
                          <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              {getSeverityIcon(anomaly.severity)}
                              <Chip
                                label={anomaly.severity}
                                color={getSeverityColor(anomaly.severity)}
                                size="small"
                                sx={{ ml: 1, mr: 1 }}
                              />
                              <Chip
                                label={anomaly.type}
                                variant="outlined"
                                size="small"
                              />
                            </Box>
                            <Typography variant="subtitle1" gutterBottom>
                              {anomaly.description}
                            </Typography>
                            {anomaly.occurrences && (
                              <Typography variant="body2" color="text.secondary">
                                Occurrences: {anomaly.occurrences}
                              </Typography>
                            )}
                            {anomaly.recommendation && (
                              <Alert severity="info" sx={{ mt: 1 }}>
                                <strong>Recommendation:</strong> {anomaly.recommendation}
                              </Alert>
                            )}
                            {anomaly.samples && anomaly.samples.length > 0 && (
                              <Accordion sx={{ mt: 1 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Typography variant="body2">
                                    View Samples ({anomaly.samples.length})
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                    {anomaly.samples.slice(0, 3).map((sample, sIdx) => (
                                      <Box key={sIdx} sx={{ mb: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                                        {sample}
                                      </Box>
                                    ))}
                                  </Box>
                                </AccordionDetails>
                              </Accordion>
                            )}
                          </Box>
                        </ListItem>
                        {index < currentAnalysis.anomalies.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}

            {/* Insights Section */}
            {currentAnalysis.insights && currentAnalysis.insights.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Insights ({currentAnalysis.insights.length})
                  </Typography>
                  <List>
                    {currentAnalysis.insights.map((insight, index) => (
                      <React.Fragment key={index}>
                        <ListItem alignItems="flex-start">
                          <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              {getCategoryIcon(insight.category)}
                              <Chip
                                label={insight.category}
                                color="primary"
                                variant="outlined"
                                size="small"
                                sx={{ ml: 1 }}
                              />
                            </Box>
                            <Typography variant="subtitle1" gutterBottom>
                              {insight.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                              {insight.description}
                            </Typography>
                            {insight.recommendation && (
                              <Alert severity="success" sx={{ mt: 1 }}>
                                <strong>Recommendation:</strong> {insight.recommendation}
                              </Alert>
                            )}
                          </Box>
                        </ListItem>
                        {index < currentAnalysis.insights.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}

            {/* No Results */}
            {(!currentAnalysis.anomalies || currentAnalysis.anomalies.length === 0) &&
             (!currentAnalysis.insights || currentAnalysis.insights.length === 0) && (
              <Alert severity="success">
                <Typography variant="h6">All Clear!</Typography>
                <Typography variant="body2">
                  No anomalies or significant issues detected in the analyzed logs.
                </Typography>
              </Alert>
            )}
          </Box>
        )}

        {/* Recent Analyses */}
        {analyses.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Recent Analyses
            </Typography>
            <List>
              {analyses.slice(0, 5).map((analysis) => (
                <ListItemButton
                  key={analysis.analysisId}
                  onClick={() => setCurrentAnalysis(analysis)}
                  selected={currentAnalysis?.analysisId === analysis.analysisId}
                >
                  <ListItemText
                    primary={`Analysis ${analysis.analysisId}`}
                    secondary={`${new Date(analysis.createdAt).toLocaleString()} - ${analysis.anomalies?.length || 0} anomalies, ${analysis.insights?.length || 0} insights`}
                  />
                  <Chip
                    label={analysis.status}
                    color={analysis.status === 'completed' ? 'success' : 'default'}
                    size="small"
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default LogAnalysisPage;
